import * as Cookies from 'js-cookie'
import { v4 as uuid } from 'uuid'

import { companyUuid as configCompanyUuid } from 'embed/constants/config'
import Api from 'embed/services/Api'
import { captureCookieCapabilityError, captureSessionInitFailedWarning } from 'embed/constants/errors'

import QueryParams from 'embed/services/QueryParams'
import { leadInitialized } from 'embed/actions'
import { QueryParamKeys } from '@evenfinancial/even-ts-static'

// Enable new session / lead behavior
export const isTrueMPS = (companyUuid?: string) =>
  companyUuid === '23b34df9-258a-447e-bd5d-c0b21ce24cb6' ||
  companyUuid === '32077204-701c-4ab0-bc44-7bc2655a607e' ||
  companyUuid === '08f5e428-f79f-4c41-b7a1-2aa0aff8734c'

export class Session {
  private readonly pCompanyUuid: string
  private pLeadUuid: string
  private readonly pSessionUuid: string
  private pSessionInitUuid: string

  constructor(companyUuid: string) {
    this.pCompanyUuid = companyUuid
    const sessionsInitUuid = QueryParams.getAll()?.sessionsInitUuid
    if (sessionsInitUuid) {
      this.pSessionInitUuid = sessionsInitUuid
      return
    }
    this.setSessionInitUuid()
  }
  // @TODO: update params to satisfy linter requirement or comment to why this needs to stay as is
  dispatch = (action: unknown) => {} // Noop by default

  getLeadUuid(): string {
    const leadUuid = this.getLeadUuidFromURL()
    if (leadUuid) {
      if (this.pLeadUuid) {
        return this.pLeadUuid
      } else {
        this.setLeadUuid(leadUuid)
        this.dispatch(leadInitialized({ uuid: leadUuid }))
        return leadUuid
      }
    } else if (isTrueMPS(this.pCompanyUuid)) {
      // NEW SESSION BEHAVIOR
      return uuid()
    } else {
      // DEFAULT BEHAVIOR
      const cookieLeadUUID: string | undefined = Cookies.get(this.leadUuidCookieKey)

      if (this.pLeadUuid) {
        return this.pLeadUuid
      } else if (cookieLeadUUID) {
        this.pLeadUuid = cookieLeadUUID
        return cookieLeadUUID
      } else {
        const newLeadUuid: string = uuid()
        this.setLeadUuid(newLeadUuid)
        this.dispatch(leadInitialized({ uuid: newLeadUuid }))
        return newLeadUuid
      }
    }
  }

  get isReturningLead(): boolean {
    return this.returningLead !== undefined
  }

  resetLeadUuid() {
    const newLeadUuid: string = uuid()
    this.setCookie(this.leadUuidCookieKey, newLeadUuid)
    if (!Cookies.get(this.leadUuidCookieKey)) {
      captureCookieCapabilityError()
    }
    this.dispatch(leadInitialized({ uuid: newLeadUuid }))
  }

  setLeadUuid(newLeadUuid: string, override = false) {
    if (!this.pLeadUuid || override) {
      this.setCookie(this.leadUuidCookieKey, newLeadUuid)
      if (!Cookies.get(this.leadUuidCookieKey)) {
        captureCookieCapabilityError()
      }
      this.pLeadUuid = newLeadUuid
    }
  }

  forceOverrideLeadUuid(newLeadUuid: string): { leadUuidHasChanged: boolean } {
    let leadUuidHasChanged
    if (this.isLeadUuidInURL() && this.getLeadUuidFromURL()) {
      leadUuidHasChanged = this.getLeadUuidFromURL() !== this.pLeadUuid
      this.pLeadUuid = this.getLeadUuidFromURL()
    } else {
      leadUuidHasChanged = this.pLeadUuid !== newLeadUuid
      this.pLeadUuid = newLeadUuid
    }
    return { leadUuidHasChanged }
  }

  forceOverrideLeadUuidWithoutCheckingUrl(newLeadUuid: string): { leadUuidHasChanged: boolean } {
    const leadUuidHasChanged = !!this.pLeadUuid && this.pLeadUuid !== newLeadUuid
    this.setLeadUuid(newLeadUuid, true)

    return { leadUuidHasChanged }
  }

  get sessionUuid(): string | undefined {
    if (this.pSessionInitUuid) {
      return this.pSessionInitUuid
    } else {
      return undefined
    }
  }

  setSessionInitUuid = async () => {
    Api.post('/uiEvent/sessionInits', {})
      .then(({ data: { sessionUuid } }) => {
        this.pSessionInitUuid = sessionUuid
      })
      .catch((e) => {
        captureSessionInitFailedWarning(e)
      })
  }

  setReturningLead = () => {
    if (!this.isReturningLead) {
      this.returningLead = new Date().getTime().toString()
    }
  }

  get trackingUuid() {
    const consumerTrackingUuid = QueryParams.get(QueryParamKeys.TrackingUuid)
    return consumerTrackingUuid ?? Cookies.get(this.trackingUuidCookieKey)
  }

  set trackingUuid(newTrackingUuid: string | undefined) {
    if (!this.trackingUuid && newTrackingUuid !== undefined) {
      this.setCookie(this.trackingUuidCookieKey, newTrackingUuid)
    }
  }

  get returningLead() {
    return Cookies.get(this.sessionReturningLeadKey)
  }

  set returningLead(timestamp: string | undefined) {
    // Set the value to the time this was set, in the future we may not want to consider them returning
    // if it's been a long enough time.
    if (timestamp !== undefined) {
      this.setCookie(this.sessionReturningLeadKey, timestamp)
    }
  }

  get companyUuid() {
    return this.pCompanyUuid
  }

  // Identifier for the cookie where leadUuid is stored
  get leadUuidCookieKey() {
    return `_eluid_${this.companyUuid}`
  }

  // Identifier for the cookie where trackingUuid is stored
  get trackingUuidCookieKey() {
    return `_evtId_${this.companyUuid}`
  }

  // Identifier for the cookie where sessionUuid is stored
  get sessionUuidCookieKey() {
    return `_esuid_${this.companyUuid}`
  }

  // Identifier for the cookie where returning lead key is stored
  get sessionReturningLeadKey() {
    return `_erl_${this.companyUuid}`
  }

  private setCookie(key: string, value: string) {
    const ONE_YEAR_MS = 31536000000
    Cookies.set(key, value, { expires: ONE_YEAR_MS, sameSite: 'none', secure: true })
  }

  private isLeadUuidInURL(): boolean {
    return !!QueryParams.get(QueryParamKeys.LeadUuid)
  }

  private getLeadUuidFromURL(): string {
    return QueryParams.get(QueryParamKeys.LeadUuid) ?? ''
  }
}

export default new Session(configCompanyUuid as string)
